import {
  Viewer,
  Cartesian3,
  Math as CMath,
  OpenStreetMapImageryProvider,
  ArcGisMapServerImageryProvider,
  ImageryLayer,
} from "cesium";

export type BaseLayerKind = "sat" | "map";
const BASE_KEY = "geaar.baseLayer";

function makeBaseProvider(kind: BaseLayerKind) {
  if (kind === "map") {
    return Promise.resolve(
      new OpenStreetMapImageryProvider({ url: "https://tile.openstreetmap.org/" }),
    );
  }
  // Esri World Imagery - token-free satellite basemap.
  return ArcGisMapServerImageryProvider.fromUrl(
    "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer",
  );
}

export function savedBaseLayer(): BaseLayerKind {
  // Satellite by default: mine sites are blank on street maps.
  return (localStorage.getItem(BASE_KEY) as BaseLayerKind) || "sat";
}

/** Swap the bottom imagery layer (the basemap), leaving project overlays alone. */
export function setBaseLayer(viewer: Viewer, kind: BaseLayerKind): void {
  localStorage.setItem(BASE_KEY, kind);
  const layers = viewer.imageryLayers;
  const old = layers.get(0);
  const fresh = ImageryLayer.fromProviderAsync(makeBaseProvider(kind), {});
  layers.add(fresh, 0);
  if (old) layers.remove(old, true);
}

// Pilbara AOI default view (Mindy / Wyloo region, FMG tenements).
export const DEFAULT_VIEW = {
  lon: 119.7,
  lat: -22.6,
  height: 60000,
};

/**
 * Create the Cesium viewer. Phase 0 uses token-free OpenStreetMap imagery over the
 * WGS84 ellipsoid (no ion token, no streamed terrain). Project terrain and imagery
 * come from converted COG/quantized-mesh assets in later phases.
 */
export function createViewer(container: HTMLElement): Viewer {
  const viewer = new Viewer(container, {
    baseLayer: ImageryLayer.fromProviderAsync(makeBaseProvider(savedBaseLayer()), {}),
    baseLayerPicker: false,
    geocoder: false,
    homeButton: false,
    sceneModePicker: false,
    navigationHelpButton: false,
    animation: false,
    timeline: false,
    infoBox: false,
    selectionIndicator: false,
    // Render ONLY when something changes (camera move, tile load, explicit requestRender),
    // instead of continuously at max framerate. Without this the GPU sits at 100% and the
    // tab eventually crashes. Dynamic updates (draw preview, layer toggles, recolour) call
    // scene.requestRender() at their change points.
    requestRenderMode: true,
    maximumRenderTimeChange: Infinity,
  });

  // Sun lighting: light the globe (terrain relief) AND the 3D models by the sun's position,
  // so the Sun tool actually moves light across the whole scene. Without this, models were lit
  // by the real-time sun (dark after WA sunset) while the flat-lit globe stayed bright - which
  // read as "the models look dark". The Sun tool sets a flattering default time.
  viewer.scene.globe.enableLighting = true;

  // 3D-on-top by default: models draw over the terrain (never cut by a mismatched DEM).
  // The view bar's "3D on top" toggle (checked by default) flips this to terrain-occludes.
  viewer.scene.globe.depthTestAgainstTerrain = false;
  viewer.camera.setView({
    destination: Cartesian3.fromDegrees(
      DEFAULT_VIEW.lon,
      DEFAULT_VIEW.lat,
      DEFAULT_VIEW.height,
    ),
    orientation: {
      heading: 0,
      pitch: CMath.toRadians(-65),
      roll: 0,
    },
  });

  return viewer;
}

/** Fly the camera to a lon/lat looking down at a 60-degree pitch. */
export function flyToLonLat(viewer: Viewer, lon: number, lat: number, height: number): void {
  viewer.camera.flyTo({
    destination: Cartesian3.fromDegrees(lon, lat, height),
    orientation: { heading: 0, pitch: CMath.toRadians(-60), roll: 0 },
  });
}
